import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, XCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const toastStyles: Record<ToastType, { container: string; icon: string; title: string; message: string; close: string; Icon: typeof CheckCircle }> = {
  success: {
    container: 'bg-success-subtle border-success',
    icon: 'text-success',
    title: 'text-success-foreground',
    message: 'text-success',
    close: 'text-success hover:bg-success-subtle focus:ring-success',
    Icon: CheckCircle,
  },
  error: {
    container: 'bg-destructive-subtle border-destructive',
    icon: 'text-destructive',
    title: 'text-destructive',
    message: 'text-destructive',
    close: 'text-destructive hover:bg-destructive-subtle focus:ring-destructive',
    Icon: XCircle,
  },
  warning: {
    container: 'bg-warning-subtle border-warning',
    icon: 'text-warning',
    title: 'text-warning-foreground',
    message: 'text-warning',
    close: 'text-warning hover:bg-warning-subtle focus:ring-warning',
    Icon: AlertCircle,
  },
  info: {
    container: 'bg-info-subtle border-info',
    icon: 'text-info',
    title: 'text-info',
    message: 'text-info',
    close: 'text-info hover:bg-info-subtle focus:ring-info',
    Icon: AlertCircle,
  },
};

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose,
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const styles = toastStyles[type];
  const { Icon } = styles;

  return (
    <div className={`pointer-events-auto w-full max-w-[420px] border rounded-lg shadow-lg p-4 ${styles.container} animate-in slide-in-from-right-full duration-300`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={`h-5 w-5 ${styles.icon}`} />
        </div>
        <div className="ml-3 w-0 flex-1">
          <p className={`text-sm font-medium ${styles.title}`}>
            {title}
          </p>
          {message && (
            <p className={`mt-1 text-sm ${styles.message}`}>
              {message}
            </p>
          )}
        </div>
        <div className="ml-4 flex-shrink-0 flex">
          <button
            className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.close}`}
            onClick={() => onClose(id)}
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
